// 관리 #/admin (관리자만 — 서버도 staff 쓰기 403) · v0.3 Admin.kt · SCREENS §1-9
// compact 섹션 펼침 목록 한 단 · medium 이상 2단(왼쪽 섹션 목록 | 오른쪽 선택 섹션) · large 직원 목록은 표
import { useEffect, useState, type ReactNode } from "react";
import { currentMe, saveSettings } from "../data/actions";
import { sessionOrder, statusLabel } from "../data/select";
import { disablePush, enablePush, fetchBackup, listBackups, pushState, runBackup, testPush, type BackupInfo, type PushState } from "../data/push";
import { useView, type View } from "../data/store";
import { St, WARN_TYPES } from "../data/types";
import { DOW, dowValue, todayStr } from "../lib/date";
import { buildXlsx, download } from "../lib/xlsx";
import { useWidthClass } from "../ui/hooks";
import { Icon } from "../ui/icons";
import { Pill, toast } from "../ui/parts";
import { Loading } from "./Dashboard";
import { openSheet } from "./sheets";

const SECTIONS = [
  { key: "staff", label: "직원 계정" },
  { key: "presets", label: "시험지 목록" },
  { key: "times", label: "시간 칩" },
  { key: "rules", label: "경고 기준" },
  { key: "notify", label: "알림" },
  { key: "data", label: "엑셀·백업" },
] as const;
type Key = (typeof SECTIONS)[number]["key"];

export function Admin() {
  const v = useView();
  const wc = useWidthClass();
  const me = currentMe();
  const [sel, setSel] = useState<Key>("staff");
  const [openKey, setOpenKey] = useState<Key | null>("staff");
  if (!v.status.loaded) return <Loading />;
  if (!me?.admin) {
    return <div className="page"><div className="soon"><h2>권한 없음</h2><p className="muted">관리 화면은 관리자 계정만 볼 수 있습니다. 서버도 직원 정보 변경을 막습니다.</p></div></div>;
  }
  const body = (k: Key): ReactNode => {
    switch (k) {
      case "staff": return <StaffSec v={v} table={wc === "large"} />;
      case "presets": return <PresetSec v={v} />;
      case "times": return <TimesSec v={v} />;
      case "rules": return <RulesSec v={v} />;
      case "notify": return <NotifySec v={v} />;
      case "data": return <DataSec v={v} />;
    }
  };
  if (wc === "compact") {
    return (
      <div className="page admin">
        {SECTIONS.map((s) => (
          <section key={s.key} className={"acc" + (openKey === s.key ? " open" : "")}>
            <button type="button" className="acc-h" aria-expanded={openKey === s.key} onClick={() => setOpenKey(openKey === s.key ? null : s.key)}>
              <span className="grow">{s.label}</span><Icon name={openKey === s.key ? "left" : "right"} size={18} />
            </button>
            {openKey === s.key ? <div className="acc-b">{body(s.key)}</div> : null}
          </section>
        ))}
      </div>
    );
  }
  return (
    <div className="page admin admin-2">
      <nav className="admin-nav" aria-label="관리 섹션">
        {SECTIONS.map((s) => (
          <button key={s.key} type="button" className={"side-item" + (sel === s.key ? " on" : "")} aria-current={sel === s.key ? "page" : undefined} onClick={() => setSel(s.key)}>{s.label}</button>
        ))}
      </nav>
      <section className="admin-body panel" aria-label={SECTIONS.find((s) => s.key === sel)!.label}>
        <h2 className="panel-h">{SECTIONS.find((s) => s.key === sel)!.label}</h2>
        {body(sel)}
      </section>
    </div>
  );
}

function StaffSec({ v, table }: { v: View; table: boolean }) {
  return (
    <>
      <div className="sec-top">
        <p className="muted small grow">로그인한 직원 이름이 일정 등록·완료 확인·경고카드 기록에 남습니다.</p>
        <button type="button" className="btn" onClick={() => openSheet({ kind: "staff", id: null })}><Icon name="plus" size={18} /> 직원 추가</button>
      </div>
      {table ? (
        <div className="table-wrap" data-scroll-x>
          <table className="tbl tbl-click">
            <thead><tr><th>이름</th><th>권한</th><th className="num">등록한 일정</th><th className="num">확인한 일정</th><th /></tr></thead>
            <tbody>{v.staff.map((s) => (
              <tr key={s.id} onClick={() => openSheet({ kind: "staff", id: s.id })}>
                <td><b>{s.name}</b></td>
                <td>{s.admin ? <Pill color="var(--color-primary)">관리자</Pill> : <span className="muted">직원</span>}</td>
                <td className="num">{v.sessions.filter((x) => x.createdBy === s.name).length}</td>
                <td className="num">{v.sessions.filter((x) => x.closedBy === s.name).length}</td>
                <td><button type="button" className="btn btn-ghost" onClick={(e) => { e.stopPropagation(); openSheet({ kind: "staff", id: s.id }); }}>수정</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : (
        <ul className="list">{v.staff.map((s) => (
          <li key={s.id}><button type="button" className="row-card" onClick={() => openSheet({ kind: "staff", id: s.id })}>
            <b>{s.name}</b>{s.admin ? <Pill color="var(--color-primary)">관리자</Pill> : null}
            <span className="grow" /><span className="muted small">등록한 일정 {v.sessions.filter((x) => x.createdBy === s.name).length}건</span>
          </button></li>
        ))}</ul>
      )}
    </>
  );
}

function PresetSec({ v }: { v: View }) {
  const [p, setP] = useState("");
  const list = v.settings.presets;
  return (
    <>
      <p className="muted small">개별일정 등록·학생 상세에서 시험지를 빠르게 고르는 목록입니다. 누르면 지웁니다.</p>
      <div className="chips mt">
        {list.map((x) => <button key={x} type="button" className="chip" aria-label={`${x} 지우기`} onClick={() => saveSettings({ presets: list.filter((y) => y !== x) })}>{x} <Icon name="close" size={16} /></button>)}
        {!list.length ? <span className="muted">비어 있음</span> : null}
      </div>
      <form className="inline-form" onSubmit={(e) => { e.preventDefault(); const t = p.trim(); if (t && !list.includes(t)) { saveSettings({ presets: [...list, t] }); setP(""); } }}>
        <input className="input" value={p} onChange={(e) => setP(e.target.value)} placeholder="새 시험지 이름 (예: 교과서 3차)" aria-label="새 시험지 이름" />
        <button type="submit" className="btn">추가</button>
      </form>
    </>
  );
}

function TimesSec({ v }: { v: View }) {
  const [t, setT] = useState("");
  const list = v.settings.times;
  return (
    <>
      <p className="muted small">등록 화면의 시간 칩입니다. 누르면 지웁니다.</p>
      <div className="chips mt">
        {list.map((x) => <button key={x} type="button" className="chip" aria-label={`${x} 지우기`} onClick={() => saveSettings({ times: list.filter((y) => y !== x) })}>{x} <Icon name="close" size={16} /></button>)}
      </div>
      <form className="inline-form" onSubmit={(e) => { e.preventDefault(); if (/^\d{2}:\d{2}$/.test(t) && !list.includes(t)) { saveSettings({ times: [...list, t].sort() }); setT(""); } }}>
        <input className="input" type="time" step={600} value={t} onChange={(e) => setT(e.target.value)} aria-label="추가할 시간" />
        <button type="submit" className="btn">추가</button>
      </form>
    </>
  );
}

function RulesSec({ v }: { v: View }) {
  const th = v.settings.warnThreshold;
  return (
    <>
      <p className="muted small">누적 경고카드가 이 기준 이상이면 대시보드·현황표에서 빨간색으로 강조합니다.</p>
      <div className="chips mt">
        {[2, 3, 4, 5].map((n) => <button key={n} type="button" className={"chip" + (th === n ? " on" : "")} aria-pressed={th === n} onClick={() => saveSettings({ warnThreshold: n })}>{n}장 이상</button>)}
      </div>
    </>
  );
}

function NotifySec({ v }: { v: View }) {
  const s = v.settings;
  return (
    <>
      <button type="button" role="switch" aria-checked={s.notify} className={"switch" + (s.notify ? " on" : "")} onClick={() => saveSettings({ notify: !s.notify })}>
        <span className="switch-track"><span className="switch-thumb" /></span><span>클리닉 시작 전에 알림</span>
      </button>
      <p className="muted small">그 시간에 올 학생 이름과 남은 시험지 수를 알려줍니다. 이 설정은 모든 기기에 같이 적용됩니다 — 안드로이드 앱은 기기 알림, 웹은 아래에서 켠 브라우저로 알림.</p>
      <div className="chips mt">
        {[0, 5, 10, 15, 30].map((m) => <button key={m} type="button" className={"chip" + (s.notifyBefore === m ? " on" : "")} aria-pressed={s.notifyBefore === m} onClick={() => saveSettings({ notifyBefore: m })}>{m === 0 ? "정각" : `${m}분 전`}</button>)}
      </div>
      <WebPush />
    </>
  );
}

const PUSH_TEXT: Record<PushState, string> = {
  on: "이 브라우저로 알림을 받고 있습니다.",
  off: "이 브라우저는 아직 알림을 받지 않습니다.",
  denied: "브라우저 설정에서 이 사이트 알림이 차단돼 있습니다. 주소창 왼쪽 자물쇠 → 알림 «허용»으로 바꾼 뒤 다시 켜 주세요.",
  unsupported: "이 브라우저는 웹 알림을 지원하지 않습니다. 아이폰·아이패드는 Safari 공유 → «홈 화면에 추가»로 설치한 앱에서 켤 수 있습니다.",
};

/** 이 브라우저(기기)의 웹 푸시 구독 — 서버 Cron 이 «시작 N분 전»에 보낸다 */
function WebPush() {
  const [st, setSt] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushState().then(setSt, () => setSt("unsupported")); }, []);
  const run = async (f: () => Promise<PushState>, ok: string) => {
    setBusy(true);
    try { const r = await f(); setSt(r); if (r === "on" || r === "off") toast(ok); }
    catch { toast("알림 설정을 바꾸지 못했습니다 — 잠시 뒤 다시 시도해 주세요", { tone: "error" }); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const r = await testPush();
      if (r.sent > 0) toast(`시험 알림을 보냈습니다 (기기 ${r.sent}대)`);
      else if (r.failed > 0) toast("알림 서비스가 아직 준비 중입니다 — 몇 초 뒤 다시 눌러 주세요", { tone: "error" }); // 갓 켠 구독은 몇 초간 거절된다
      else toast("알림을 켠 기기가 없습니다", { tone: "error" });
    } catch { toast("시험 알림을 보내지 못했습니다", { tone: "error" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="push-box mt" data-push={st ?? "loading"}>
      <p className="label">이 브라우저로 알림 받기</p>
      <p className="muted small">{st ? PUSH_TEXT[st] : "확인 중…"}</p>
      <div className="row-wrap mt">
        {st === "on"
          ? <button type="button" className="btn" disabled={busy} onClick={() => run(disablePush, "이 브라우저 알림을 껐습니다")}>이 브라우저 알림 끄기</button>
          : <button type="button" className="btn btn-primary" disabled={busy || st === "unsupported" || st === null} onClick={() => run(enablePush, "이 브라우저로 알림을 받습니다")}>이 브라우저 알림 켜기</button>}
        <button type="button" className="btn btn-ghost" disabled={busy || st !== "on"} onClick={test}>지금 알림 시험 보내기</button>
      </div>
    </div>
  );
}

function DataSec({ v }: { v: View }) {
  return (
    <>
      <p className="label">엑셀 다운로드</p>
      <p className="muted small">학생 현황 · 개별일정 전체 이력 · 클리닉 항목 · 경고카드 4개 시트 (안드로이드 앱과 같은 구성)</p>
      <button type="button" className="btn btn-primary mt" onClick={() => { exportExcel(v); toast("엑셀 파일을 내려받았습니다"); }}>엑셀(.xlsx)로 내려받기</button>
      <ServerBackups />
      <p className="label">이 화면 데이터 백업</p>
      <p className="muted small">지금 화면에 있는 모든 데이터를 JSON 파일 하나로 내려받습니다(PIN 은 들어가지 않음).</p>
      <button type="button" className="btn mt" onClick={() => { exportJson(v); toast("백업 파일을 내려받았습니다"); }}>백업 파일(.json) 내려받기</button>
    </>
  );
}

const kb = (n: number) => (n < 1024 ? `${n} B` : `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`);
const backupLabel = (b: BackupInfo) => (b.key.startsWith("daily/") ? "매일 자동" : "즉시 실행");

/** 서버 자동 백업(R2) 목록 — 매일 03:00 이후 1회 + 관리자 «지금 백업» */
function ServerBackups() {
  const [list, setList] = useState<BackupInfo[] | null>(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => listBackups().then((l) => { setList(l); setErr(false); }, () => setErr(true));
  useEffect(() => { load(); }, []);
  const now = async () => {
    setBusy(true);
    try { const b = await runBackup(); toast(`서버에 백업했습니다 · ${kb(b.size)}`); await load(); }
    catch { toast("서버 백업을 만들지 못했습니다", { tone: "error" }); }
    finally { setBusy(false); }
  };
  const get = async (b: BackupInfo) => {
    try { download(`centium-${b.key.replace("/", "-")}`, "application/json", await fetchBackup(b.key)); }
    catch { toast("백업 파일을 받지 못했습니다", { tone: "error" }); }
  };
  const shown = (list ?? []).slice(0, 10);
  return (
    <div className="backup-box" data-backups={list ? list.length : -1}>
      <p className="label">서버 자동 백업</p>
      <p className="muted small">서버가 매일 새벽 3시 이후 한 번 전체 데이터를 보관합니다(직원 PIN 제외). 최근 10건을 보여 줍니다.</p>
      {err ? <p className="muted small">목록을 불러오지 못했습니다.</p> : null}
      {list && list.length === 0 ? <p className="muted small">아직 백업이 없습니다.</p> : null}
      {shown.length > 0 ? (
        <ul className="backup-list">
          {shown.map((b) => (
            <li key={b.key}>
              <span className="grow"><b>{ts(b.at)}</b> <span className="muted small">{backupLabel(b)} · {kb(b.size)}</span></span>
              <button type="button" className="btn btn-ghost" onClick={() => get(b)} aria-label={`${ts(b.at)} 백업 내려받기`}>받기</button>
            </li>
          ))}
        </ul>
      ) : null}
      <button type="button" className="btn mt" disabled={busy} onClick={now}>지금 서버에 백업</button>
    </div>
  );
}

const stamp = () => `${todayStr()}_${String(Date.now() % 100000).padStart(5, "0")}`;
const ts = (ms: number | null | undefined) => {
  if (!ms) return "";
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** v0.3 Export.excel 과 같은 4시트 */
export function exportExcel(v: View) {
  const st = new Map(v.students.map((s) => [s.id, s]));
  const nm = (id: string) => st.get(id)?.name ?? "(삭제됨)";
  const sc = (id: string) => st.get(id)?.school ?? "";
  const sessions = [["날짜", "요일", "시간", "학생", "학교·학년", "상태", "클리닉 지각", "등록 직원", "등록 시각", "마감 직원", "이 일정에서 통과한 항목", "메모"],
    ...[...v.sessions].sort(sessionOrder).map((s) => [s.date, DOW[dowValue(s.date) - 1], s.time, nm(s.studentId), sc(s.studentId), s.status, s.late ? "지각" : "",
      s.createdBy, ts(s.createdAt), s.closedBy ?? "", v.items.filter((i) => i.doneSession === s.id).map((i) => `[${i.week}] ${i.title}`).join(" / "), s.note])];
  const items = [["학생", "학교·학년", "주차", "클리닉 항목", "상태", "등록 직원", "등록 시각", "통과 시각", "확인 직원"],
    ...[...v.items].sort((a, b) => nm(a.studentId).localeCompare(nm(b.studentId), "ko") || a.week.localeCompare(b.week, "ko"))
      .map((i) => [nm(i.studentId), sc(i.studentId), i.week, i.title, i.done ? "통과" : "미완료", i.createdBy, ts(i.createdAt), ts(i.doneAt), i.doneBy ?? ""])];
  const warns = [["날짜", "학생", "학교·학년", "종류", "메모", "기록 직원"],
    ...[...v.warns].sort((a, b) => a.date.localeCompare(b.date)).map((w) => [w.date, nm(w.studentId), sc(w.studentId), w.type, w.note, w.createdBy])];
  const board = [["학생", "학교·학년", "미완료 클리닉", "경고카드 합계", ...WARN_TYPES, "전체 일정", "완료", "미완료", "노쇼"],
    ...v.students.map((s) => {
      const ws = v.warns.filter((w) => w.studentId === s.id);
      const ss = v.sessions.filter((x) => x.studentId === s.id);
      return [s.name, s.school, `${v.items.filter((i) => i.studentId === s.id && !i.done).length}`, `${ws.length}`,
        ...WARN_TYPES.map((t) => `${ws.filter((w) => w.type === t).length}`),
        `${ss.length}`, `${ss.filter((x) => x.status === St.DONE).length}`, `${ss.filter((x) => x.status === St.PARTIAL).length}`, `${ss.filter((x) => x.status === St.NOSHOW).length}`];
    })];
  const bytes = buildXlsx([["학생 현황", board], ["개별일정", sessions], ["클리닉 항목", items], ["경고카드", warns]]);
  download(`센티움클리닉_${stamp()}.xlsx`, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", bytes);
}

function exportJson(v: View) {
  const data = { ver: 4, exportedAt: new Date().toISOString(), staff: v.staff, students: v.students, items: v.items, sessions: v.sessions, warns: v.warns, settings: v.settings,
    note: `상태 표기: ${[St.PLAN, St.DONE, St.PARTIAL, St.NOSHOW].map(statusLabel).join("/")} · PIN 은 서버에만 해시로 있어 백업에 없음` };
  download(`센티움클리닉_백업_${stamp()}.json`, "application/json", JSON.stringify(data, null, 2));
}

